// The B10 screens' registration onto the shell's one view registry (`shell/viewRegistry.ts`, lane contract C-29). Each
// B10 screen directory registers itself from its own `register.ts` (loaded by `./register.ts`, which `shell/views.ts`
// imports with the other lanes' lines); App.tsx hosts every registered view in the shell's `.mc` island, so a B10 screen
// draws no McRoot of its own (McPage) and puts its V1 parts (the not-found screen) in a V1Slot. Home widgets are not
// here: they register on B1's Home through `./home/register.ts` (C-B10-3).
//
// A B10 route is gated on every path its pattern matches, not only the landing route: a reader whose manifest lists
// none of its gates gets the not-found screen (N02), as the console's and System Admin's deep routes require.
import { createElement, lazy, type ComponentType } from "react";
import type { Route } from "@/router";
import { useSession } from "@/session/useSession";
import { V1Slot } from "@/shell/island";
import { registerView as registerShellView } from "@/shell/viewRegistry";

// The V1 not-found screen loads on first use: its module reads the window at import (the router), which a registration
// read under SSR (the lane's node suites) must not.
const NotFound = lazy(() => import("@/views/NotFound").then((module) => ({ default: module.NotFound })));

export type B10View = ComponentType<{ route: Route }>;

export interface ViewOpts {
  lane: "B10";
  specificity?: number; // a higher one wins over a lower one; equal ones in registration order (the earlier lane's)
  gates?: readonly string[]; // the manifest tools of which the reader must hold one, else not-found (N02)
}

/** What B10 puts in the registry for a screen: its gate around it, carrying the screen it stands for (`screen`), so a
 * reader of the registry's `matchView` sees B10's own screen. */
export type B10Route = B10View & { readonly screen: B10View };

function gated(Screen: B10View, gates: readonly string[]): B10Route {
  function B10Route({ route }: { route: Route }) {
    const { can } = useSession();
    if (gates.length > 0 && !gates.some(can)) return createElement(V1Slot, null, createElement(NotFound, { route: route.raw }));
    return createElement(Screen, { route });
  }
  B10Route.displayName = `B10(${Screen.displayName ?? Screen.name})`;
  B10Route.screen = Screen;
  return B10Route;
}

/** Registers a screen for the routes whose path (`view/part/…`, no query) the pattern matches. A duplicate pattern throws. */
export function registerView(pattern: RegExp, component: B10View, opts: ViewOpts): void {
  if (opts.lane !== "B10") throw new Error(`registerView: lane ${String(opts.lane)} is not B10`);
  const gates = [...(opts.gates ?? [])];
  registerShellView(pattern, gated(component, gates), { lane: "B10", specificity: opts.specificity ?? 0, gates });
}

/** The query of the route: the part of `route.raw` after the first "?". */
export function queryOf(route: Route): URLSearchParams {
  const at = route.raw.indexOf("?");
  return new URLSearchParams(at < 0 ? "" : route.raw.slice(at + 1));
}
